'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DEAIChatBox } from '@/components/deai-chatbox';
import { StartOverlay } from '@/components/deai-startoverlay';
import { AnimatedGradientBg } from '@/components/animated-gradient-bg';
import { VerificationModal } from '@/components/ui/verification-modal';
import { useSocket } from '@/hooks/socket-context';
import { createSpeaker } from '@akavirtual/deai-web-core';
import type { MessageType, MessageData } from '@/components/deai-chatbox-message';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/auth-context';
import { useVaultVenues } from '@/hooks/useVaultVenues';
import type { Venue, ChatThemeId } from '@/types/venue';
import { CHAT_THEMES, DEFAULT_CHAT_THEME } from '@/types/venue';
import { AlertTriangle, X, Palette } from 'lucide-react';

// Define the shape of a chat message
type ChatMessage = {
  id: string;
  role: string;
  content: string;
  messageType?: MessageType;
  messageData?: MessageData;
  turnId?: string; // Track which turn this message belongs to
  [key: string]: any; // Allow additional properties
};

type AgentThinkingState = {
  type: string;
  status: string;
  toolName?: string;
};


// Individual DEAI Chat component with its own WebSocket context
export type DEAIChatInstanceProps = {
  instanceName?: string,
  onStart?: () => void,
  onStarted?: () => void,
  isStarted?: boolean,
  venueId?: string, // Venue ID (UUID) from URL for multi-tenant chat
  vanitySlug?: string, // Vanity slug from URL (for SEO)
  shortCode?: string, // Short code from KV lookup (preferred identifier for chat)
  orgSlug?: string, // Used to make service calls
  persona?: string, // Optional persona override (defaults to 'concierge' for venues)
};

export const DEAIChatInstance: React.FC<DEAIChatInstanceProps> = ({
  instanceName = "Chat Instance",
  onStart = () => {},
  onStarted = () => {},
  isStarted: propIsStarted,
  venueId,
  vanitySlug,
  shortCode,
  orgSlug,
  persona = 'concierge', // Default to concierge persona for venue chats
}) => {
  const { t, language } = useI18n();
  const [internalStarted, setInternalStarted] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [agentThinking, setAgentThinking] = useState<AgentThinkingState | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<ChatThemeId>(DEFAULT_CHAT_THEME);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [isTTSMuted, setIsTTSMuted] = useState(false);

  // Auth and venue state for owner banner
  const { isAuthenticated, user, session } = useAuth();
  const { getVenue, updateVenue } = useVaultVenues();
  const [venueData, setVenueData] = useState<Venue | null>(null);
  const [showOwnerBanner, setShowOwnerBanner] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  // Use either prop or internal state for isStarted
  const isStarted = propIsStarted !== undefined ? propIsStarted : internalStarted;

  const {
    connect,
    disconnect,
    sendMessage,
    isConnected,
    isConnecting,
    sessionId,
    subscribeToServerMessage,
    unsubscribeFromServerMessage,
    isSessionEnded,
    restartSession
  } = useSocket();

  // Initialize VRM Avatar Speaker for audio playback
  const speaker = useRef<ReturnType<typeof createSpeaker> | null>(null);
  // Guard against sending 'start' more than once per connection lifecycle
  const hasSentStart = useRef(false);

  // Initialize the speaker when component mounts
  useEffect(() => {
    // Create the speaker instance
    speaker.current = createSpeaker();

    // Cleanup function
    return () => {
      // Stop any ongoing speech when component unmounts
      if (speaker.current) {
        speaker.current.speak(undefined, true); // interrupt=true to stop
      }
    };
  }, []);

  // Fetch venue data when venueId is provided
  useEffect(() => {
    const fetchVenueData = async () => {
      if (!venueId) return;

      try {
        const venue = await getVenue(venueId);
        setVenueData(venue);

        // Apply saved theme from venue (or use default)
        if (venue.themeId) {
          setCurrentTheme(venue.themeId);
        }

        // Debug logging for owner status banner
        console.log('[DEAIChatInstance] === Owner Banner Debug ===');
        console.log('[DEAIChatInstance] venue:', venue);
        console.log('[DEAIChatInstance] venue.ownerId:', venue.ownerId);
        console.log('[DEAIChatInstance] venue.status:', venue.status);
        console.log('[DEAIChatInstance] venue.themeId:', venue.themeId);
        console.log('[DEAIChatInstance] user:', user);
        console.log('[DEAIChatInstance] user?.id:', user?.id);
        console.log('[DEAIChatInstance] isAuthenticated:', isAuthenticated);

        // Check if the current user is the owner of this venue
        // If venue has no ownerId (legacy data), treat authenticated users as potential owners
        const isVenueOwner = isAuthenticated && user && (
          venue.ownerId === user.id || // Exact match
          !venue.ownerId // Legacy venue without ownerId - show banner for any authenticated user
        );

        console.log('[DEAIChatInstance] isVenueOwner (computed):', isVenueOwner);
        console.log('[DEAIChatInstance] ===========================');

        if (isVenueOwner) {
          setIsOwner(true);
        }
      } catch (error) {
        console.log(`${instanceName}: Unable to fetch venue data (may not be logged in or venue not found)`, error);
        // Silent fail - venue data is optional for displaying status banner
      }
    };

    fetchVenueData();
  }, [venueId, isAuthenticated, user, getVenue, instanceName]);

  // Add a message to the chat, handling assistant message aggregation
  const addMessage = useCallback((newMessage: ChatMessage) => {
    setChatMessages(prevMessages => {
      // If the message has an ID (which indicates it can be merged with other messages with the same ID),
      // check if there's already a message with the same ID
      if (newMessage.id) {
        const existingMessageIndex = prevMessages.findIndex(msg => msg.id === newMessage.id);
        
        if (existingMessageIndex !== -1) {
          // If a message with the same ID exists, merge the content
          const updatedMessages = [...prevMessages];
          updatedMessages[existingMessageIndex] = {
            ...updatedMessages[existingMessageIndex],
            content: updatedMessages[existingMessageIndex].content + newMessage.content,
          };
          return updatedMessages;
        }
      }
      // If the message doesn't have an ID or no existing message with the same ID was found,
      // add it as a new message
      return [...prevMessages, newMessage];
    });
  }, []);

  // Set up message handler
  useEffect(() => {
    const handleMessage = (data: any) => {
      let messageToAdd: ChatMessage;
      const messageTurnId = data.turnId; // Extract turnId from server message

      // Handle agent thinking/processing events
      if (data.action === 'agent_thinking') {
        setAgentThinking({
          type: data.type,
          status: data.status,
          toolName: data.toolName
        });
        // Clear thinking state when agent tool ends
        if (data.type === 'agent_tool_end') {
          setTimeout(() => setAgentThinking(null), 300);
        }
        return;
      }

      // Clear thinking state when actual message arrives
      if (data.action === 'message') {
        setAgentThinking(null);
      }

      // Handle audio messages from server (base64 encoded audio data)
      if (data.action === 'audio' && data.audio) {
        try {
          // Convert base64 audio data to Blob
          const byteString = atob(data.audio);
          const mimeString = data.format === 'mp3' ? 'audio/mp3' : 'audio/wav';
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const audioBlob = new Blob([ab], { type: mimeString });
          
          // Create message for display and playback
          messageToAdd = {
            id: data.id || `audio_${Date.now()}`,
            role: data.role || 'assistant',
            content: data.text || 'Audio message',
            audioBlob: audioBlob,
            isAudio: true,
            turnId: messageTurnId
          };
          
          // Play the audio using the speaker (unless muted)
          if (speaker.current && !isTTSMuted) {
            console.log(`${instanceName}: Playing received audio message`);
            speaker.current.speak(audioBlob, data.interrupt || false);
          } else if (isTTSMuted) {
            console.log(`${instanceName}: Audio received but TTS is muted`);
          }
        } catch (e) {
          console.error("Error decoding audio:", e);
        }
      } 
      // Handle system event messages (interactive UI components)
      else if (data.action === 'system_event' && data.data) {
        const eventData = data.data;
        const eventType = eventData.type;

        console.log('[DEAI-CHAT] Received system_event:', eventType);
        console.log('[DEAI-CHAT] Full event data:', data);

        // Create an interactive message based on the event type
        // Use turnId-based ID for consistency (system events append to the same turn)
        const messageId = messageTurnId
          ? `assistant_${messageTurnId}_${eventType}_${Date.now()}`
          : `system_${eventType}_${Date.now()}`;

        messageToAdd = {
          id: messageId,
          role: 'assistant',
          content: '', // Interactive messages don't need text content
          turnId: messageTurnId
        };

        switch (eventType) {
          case 'carousel':
            messageToAdd.messageType = 'carousel';
            messageToAdd.messageData = {
              items: eventData.items || []
            };
            break;

          case 'button_template':
            messageToAdd.messageType = 'button_template';
            messageToAdd.messageData = {
              title: eventData.title,
              description: eventData.description,
              actions: eventData.actions || []
            };
            break;

          case 'confirm_template':
            messageToAdd.messageType = 'confirm_template';
            messageToAdd.messageData = {
              message: eventData.message || '',
              confirmAction: eventData.confirmAction,
              cancelAction: eventData.cancelAction
            };
            break;

          case 'summary_template':
            messageToAdd.messageType = 'summary_template';
            messageToAdd.messageData = {
              title: eventData.title,
              fields: eventData.fields || []
            };
            break;

          case 'location':
            console.log('[DEAI-CHAT] Processing location event:', {
              title: eventData.title,
              address: eventData.address,
              latitude: eventData.latitude,
              longitude: eventData.longitude,
              googleMapsUrl: eventData.googleMapsUrl
            });
            messageToAdd.messageType = 'location';
            messageToAdd.messageData = {
              title: eventData.title,
              address: eventData.address,
              latitude: eventData.latitude,
              longitude: eventData.longitude,
              googleMapsUrl: eventData.googleMapsUrl
            };
            console.log('[DEAI-CHAT] Location messageToAdd:', messageToAdd);
            break;

          case 'verification_form':
            // Show the verification modal
            console.log('[DEAI-CHAT] Received verification_form event, showing modal');
            console.log('[DEAI-CHAT] Event data:', eventData);
            setShowVerificationModal(true);
            setVerificationPending(true);
            // Don't add a message for this event
            return;

          default:
            // Unknown system event type, skip
            return;
        }

        addMessage(messageToAdd);
      }
      // Handle session ended (idle timeout)
      else if (data.action === 'session_ended') {
        console.log('[DEAI-CHAT] Session ended:', data.reason);
        // The service_complete message will be added when we receive the goodbye message with isGoodbye flag
        return;
      }
      // Handle text messages from server
      else if (data.action === 'message' && data.content) {
        // Check if this is a goodbye message (session ending due to idle timeout)
        if (data.isGoodbye) {
          console.log('[DEAI-CHAT] Received goodbye message');
          messageToAdd = {
            id: `goodbye_${Date.now()}`,
            role: 'assistant',
            content: data.content,
            messageType: 'service_complete' as const,
            messageData: {
              goodbyeMessage: data.content,
              buttonLabel: 'Start New Session'
            }
          };
          addMessage(messageToAdd);
          return;
        }

        // Use turnId as the ID for assistant messages so they all merge into one bubble
        // For user messages, use the provided ID
        const messageId = data.role === 'assistant' && messageTurnId
          ? `assistant_${messageTurnId}`
          : (data.id ? data.id.toString() : `msg_${Date.now()}`);

        messageToAdd = {
          id: messageId,
          role: data.role || 'assistant',
          content: data.content,
          turnId: messageTurnId
        };

        addMessage(messageToAdd);
      }
    };

    subscribeToServerMessage(handleMessage);

    return () => {
      unsubscribeFromServerMessage(handleMessage);
    };
  }, [subscribeToServerMessage, unsubscribeFromServerMessage, addMessage, instanceName, speaker, isTTSMuted]);

  // Handle sending messages
  const handleSendMessage = (message: any) => {
    // Generate unique turnId for this conversation turn
    const turnId = Date.now().toString();

    let messageToSend;

    if (typeof message === 'object' && message.content) {
      // This is a text message object
      messageToSend = {
        action: 'message',
        role: 'user',
        content: message.content,
        turnId: turnId
      };
    } else if (typeof message === 'string') {
      // This is a plain text message
      messageToSend = {
        action: 'message',
        role: 'user',
        content: message,
        turnId: turnId
      };
    } else {
      // Fallback for other types
      messageToSend = {
        action: 'message',
        role: 'user',
        content: String(message),
        turnId: turnId
      };
    }

    // Create a user message and add it to the chat
    const userMessage: ChatMessage = {
      id: `user_${turnId}`,
      role: messageToSend.role || "user",
      content: messageToSend.content,
      turnId: turnId
    };

    // Add user message to chat
    setChatMessages(prev => [...prev, userMessage]);

    // Send it through WebSocket
    if (isConnected) {
      sendMessage(messageToSend);
    } else {
      console.log("Socket not connected, message not sent");
    }
  };

  // Handle sending audio messages
  const handleSendAudio = (audioBlob: Blob) => {
    // Send it through WebSocket
    if (isConnected) {
      sendMessage(audioBlob);
    } else {
      console.log("Socket not connected, audio message not sent");
    }
  };

  // Handle audio available
  const handleAudioAvailable = (blob: Blob) => {
    console.log(`${instanceName} received audio blob:`, blob);
    // Send the audio through the dedicated audio function
    handleSendAudio(blob);
  };

  // Handle verification completion
  const handleVerificationComplete = (verificationNonce: string) => {
    console.log(`${instanceName}: Verification completed with nonce: ${verificationNonce}`);

    // Send the verification nonce to the server
    if (isConnected) {
      sendMessage({
        action: 'verification_complete',
        verificationNonce: verificationNonce
      });
    }

    setShowVerificationModal(false);
    setVerificationPending(false);
  };

  // Handle verification modal close without completion
  const handleVerificationClose = () => {
    console.log(`${instanceName}: Verification modal closed without completion`);
    setShowVerificationModal(false);
    // Keep verificationPending true so user can reopen
  };

  // Handle verification cancellation
  const handleVerificationCancel = () => {
    console.log(`${instanceName}: User cancelled verification`);

    // Clear verification state
    setShowVerificationModal(false);
    setVerificationPending(false);

    // Notify the server that verification was cancelled
    if (isConnected) {
      sendMessage({
        action: 'verification_cancelled',
        message: 'The user has decided not to complete the verification and reservation.'
      });
    }
  };

  // Handle theme change - applies locally and saves to venue
  const handleThemeChange = async (themeId: ChatThemeId) => {
    // Apply theme immediately for instant feedback
    setCurrentTheme(themeId);
    setShowThemeSelector(false);

    // Save to venue if we have a venueId and are authenticated
    if (venueId && isAuthenticated) {
      try {
        console.log(`[DEAIChatInstance] Saving theme ${themeId} to venue ${venueId}`);
        await updateVenue(venueId, { themeId });
        // Update local venue data to keep in sync
        setVenueData(prev => prev ? { ...prev, themeId } : null);
      } catch (error) {
        console.error(`[DEAIChatInstance] Failed to save theme:`, error);
        // Theme is already applied locally, so user still sees the change
        // but it won't persist if they refresh
      }
    }
  };

  // Handle TTS mute toggle
  const handleTTSMuteToggle = () => {
    setIsTTSMuted(prev => {
      const newMuted = !prev;
      console.log(`[DEAIChatInstance] TTS ${newMuted ? 'muted' : 'unmuted'}`);
      // If muting, stop any currently playing audio
      if (newMuted && speaker.current) {
        speaker.current.speak(undefined, true); // interrupt=true to stop
      }
      return newMuted;
    });
  };

  // When started, connect to WebSocket if not already connected
  // Skip if session has ended (idle timeout) to prevent auto-reconnect
  useEffect(() => {
    if (isStarted && !isConnected && !isConnecting && !isSessionEnded) {
      const connectToWebSocket = async () => {
        try {
          // Connect using the SocketProvider context
          console.log(`${instanceName}: Connecting`);
          await connect();
          onStarted(); // Notify that we've started
        } catch (error) {
          console.error(`Connection error for ${instanceName}:`, error);
        }
      };

      connectToWebSocket();
    }
  }, [isStarted, isConnected, isConnecting, isSessionEnded, connect, instanceName, onStarted]);

  // Keep a stable ref to sendMessage so we don't need it in the effect dep array
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  // When connected, send start action with venue/persona info — once per component lifetime
  // hasSentStart is never reset mid-session; it only resets when the component remounts
  // (i.e., on a deliberate session restart), preventing opener spam on reconnects/flickers.
  useEffect(() => {
    if (isConnected && isStarted && !isSessionEnded && !hasSentStart.current) {
      hasSentStart.current = true;
      console.log(`${instanceName}: Sending start action with shortCode: ${shortCode}, venueId: ${venueId}, orgSlug: ${orgSlug}, persona: ${persona}, authToken: ${session?.token ? 'yes' : 'no'}`);
      sendMessageRef.current({
        action: 'start',
        persona: persona,
        venueId: venueId,
        vanitySlug: vanitySlug,
        shortCode: shortCode, // Short code for KV-based lookup
        orgSlug: orgSlug, // Organization slug for RAG API calls
        serviceSlug: shortCode, // Service slug (same as shortCode) for RAG API calls
        authToken: session?.token, // Pass auth token for venue data fetching
      });
    }
  }, [isConnected, isStarted, isSessionEnded, venueId, persona, vanitySlug, shortCode, orgSlug, instanceName, session?.token]);

  return (
    <div className={`w-full h-full h-[500px] flex flex-col items-center relative overflow-hidden ${currentTheme} transition-colors duration-500`}>
      {/* Animated gradient background */}
      <AnimatedGradientBg />

      {/* Glass morphism container - narrow on desktop, full width on mobile */}
      <div className="absolute inset-0 md:inset-4 md:max-w-2xl md:mx-auto bg-[color:var(--chat-overlay-bg)] backdrop-blur-xl md:rounded-3xl md:shadow-2xl md:shadow-[color:var(--chat-overlay-shadow)] md:border md:border-[color:var(--chat-overlay-border)] flex flex-col overflow-hidden transition-colors duration-500">
        {/* Owner Status Banner - Shows for authenticated owners when venue is not published */}
        {isAuthenticated && isOwner && venueData && (venueData.status || 'draft') !== 'published' && showOwnerBanner && (() => {
          const venueStatus = venueData.status || 'draft';
          return (
            <div className={`relative z-20 px-4 py-3 flex items-center justify-between gap-3 ${
              venueStatus === 'draft'
                ? 'bg-yellow-50 border-b border-yellow-200'
                : 'bg-gray-50 border-b border-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${
                  venueStatus === 'draft' ? 'text-yellow-600' : 'text-gray-600'
                }`} />
                <div>
                  <p className={`text-sm font-medium ${
                    venueStatus === 'draft' ? 'text-yellow-800' : 'text-gray-800'
                  }`}>
                    {venueStatus === 'draft' ? 'Draft Mode' : 'Archived'}
                  </p>
                  <p className={`text-xs ${
                    venueStatus === 'draft' ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {venueStatus === 'draft'
                      ? 'This venue is not visible to the public yet.'
                      : 'This venue is archived and hidden from the public.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Theme Selector */}
                <div className="relative">
                  <button
                    onClick={() => setShowThemeSelector(!showThemeSelector)}
                    className={`p-1 rounded-full transition-colors ${
                      venueStatus === 'draft'
                        ? 'text-yellow-600 hover:bg-yellow-200'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Change Theme"
                  >
                    <Palette className="h-4 w-4" />
                  </button>
                  {showThemeSelector && (
                    <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 grid grid-cols-1 overflow-hidden">
                      {CHAT_THEMES.map(theme => (
                        <button
                          key={theme.id}
                          onClick={() => handleThemeChange(theme.id)}
                          className={`px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${currentTheme === theme.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                        >
                          <span className={`w-3 h-3 rounded-full ${theme.color}`}></span>
                          {theme.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <a
                  href={`/${language}/edit/${venueId}`}
                  className={`text-xs px-3 py-1 rounded-full font-medium ${
                    venueStatus === 'draft'
                      ? 'bg-yellow-200 text-yellow-800 hover:bg-yellow-300'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Manage
                </a>
                <button
                  onClick={() => setShowOwnerBanner(false)}
                  className={`p-1 rounded-full ${
                    venueStatus === 'draft'
                      ? 'text-yellow-600 hover:bg-yellow-200'
                      : 'text-gray-600 hover:bg-gray-200'
                  } transition-colors`}
                  title="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })()}

        <StartOverlay
          soundURL="/start-sound.mp3"
          initialCanStart={!isStarted}
          fitParent={true}
          onStart={() => {
            onStart(); // Call the external onStart if provided
            if (propIsStarted === undefined) {
              // Only update internal state if external prop is not controlling it
              setInternalStarted(true);
            }
          }}
        />
        {isStarted && (
          <DEAIChatBox
            className="flex-grow z-10"
            chatMessages={chatMessages}
            isTyping={isTyping}
            agentThinking={agentThinking}
            onSendMessage={handleSendMessage}
            onAudioAvailable={handleAudioAvailable}
            onRestartSession={restartSession}
            isSessionEnded={isSessionEnded}
            isTTSMuted={isTTSMuted}
            onTTSMuteToggle={handleTTSMuteToggle}
            restaurantInfo={venueData ? {
              name: venueData.title,
              hours: (() => {
                // Check if hours data exists and has entries
                if (!venueData.hours || venueData.hours.length === 0) {
                  return 'Hours not available';
                }
                const openDays = venueData.hours.filter(h => !h.isClosed).length;
                if (openDays === 7) {
                  return 'Open 7 days';
                } else if (openDays === 0) {
                  return 'Currently closed';
                } else {
                  return `Open ${openDays} days/week`;
                }
              })(),
              location: venueData.address
                ? `${venueData.address.street}, ${venueData.address.city}, ${venueData.address.state}`
                : undefined,
              phone: venueData.contact?.phone || undefined,
            } : undefined}
          />
        )}
      </div>

      {/* Verification Modal */}
      {showVerificationModal && (
        <VerificationModal
          onClose={handleVerificationClose}
          onVerificationComplete={handleVerificationComplete}
        />
      )}

      {/* Verification Pending Banner */}
      {verificationPending && !showVerificationModal && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-40 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3">
          <span className="font-medium">{t('banner.verificationRequired')}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setShowVerificationModal(true)}
              className="bg-white text-blue-600 px-4 py-1 rounded-full font-semibold hover:bg-blue-50 transition-colors"
            >
              {t('banner.completeButton')}
            </button>
            <button
              onClick={handleVerificationCancel}
              className="bg-transparent border border-white text-white px-4 py-1 rounded-full font-semibold hover:bg-white/10 transition-colors"
            >
              {t('banner.cancelButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};